/**
 * Team service
 * Handles all team-related API calls
 */

import { API_ENDPOINTS } from './endpoints';
import { fetchApi } from '@/lib/utils/api';
import type { Team, Player, PublicTeam } from '@/lib/types/auction';
import { showSuccessToast } from '@/lib/utils/toast';

export interface CreateTeamInput {
  name: string;
  logo: string;
  teamOwner: {
    firstName: string;
    lastName: string;
    email: string;
    password?: string;
  };
  budget: number;
  maxPlayersPerTeam?: number;
  tournamentOwnerId?: string;
  tournamentId?: string;
}

export interface UpdateTeamInput {
  name?: string;
  logo?: string;
  budget?: number;
}

export const teamService = {
  /**
   * Create new team
   */
  async create(data: CreateTeamInput): Promise<Team> {
    try {
      const response = await fetchApi<Team>(API_ENDPOINTS.TEAMS.BASE, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      showSuccessToast('team', 'create');
      return response.result;
    } catch (error) {
      console.error('Team creation failed:', error);
      throw error;
    }
  },

  /**
   * Update team
   */
  async update(id: string, data: UpdateTeamInput): Promise<Team> {
    const response = await fetchApi<Team>(API_ENDPOINTS.TEAMS.PROFILE(id), {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    showSuccessToast('team', 'update');
    return response.result;
  },

  /**
   * Delete team
   */
  async delete(id: string): Promise<void> {
    await fetchApi(API_ENDPOINTS.TEAMS.PROFILE(id), {
      method: 'DELETE',
    });
    showSuccessToast('team', 'delete');
  },

  /**
   * Get team by ID
   */
  async getById(id: string): Promise<Team> {
    const response = await fetchApi<Team>(API_ENDPOINTS.TEAMS.PROFILE(id), {
      requireAuth: false,
    });
    return response.result;
  },

  /**
   * Get team players
   */
  async getPlayers(teamId: string): Promise<Player[]> {
    const response = await fetchApi<Player[]>(
      API_ENDPOINTS.TEAMS.PLAYERS(teamId),
      {
        requireAuth: false,
      }
    );
    return response.result;
  },

  /**
   * Get my team
   */
  async getMyTeam(): Promise<Team> {
    const response = await fetchApi<Team>(API_ENDPOINTS.TEAMS.MY_TEAM);
    return response.result;
  },

  /**
   * Get public teams sitemap
   */
  async getPublicTeamsSitemap(): Promise<PublicTeam[]> {
    const response = await fetchApi<{ result: PublicTeam[] }>(
      API_ENDPOINTS.TEAMS.SITEMAP,
      { requireAuth: false }
    );
    return Array.isArray(response.result) ? response.result : [];
  },
};
